import React, { useEffect, useState, useCallback, use } from 'react';
import { StatusCodes } from 'http-status-codes';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import TabsGroup from 'components/common/tabs-group-component';
import { ReactComponent as CloseButton } from 'icons/close-button.svg';
import StatementHeader from 'pages/side-by-side-compare/left-side/_left-side-statement-subheader';
import BalanceSheetView from 'pages/side-by-side-compare/left-side/balance-sheet-view';
import IncomeStatementView from 'pages/side-by-side-compare/left-side/income-statement-view';
import {
  getTrialBalanceDetails,
  setTrialBalanceTableTab,
  updatePendingAutoTieoutTBStep,
} from 'store/actions/trial-balance/trial-balance-action';
import { setAutoTieoutStatusForBanner } from 'store/actions/auto-tieout/workpaper-details-action';
import {
  TRIAL_BALANCE_TABS_ARRAY,
  TRIAL_BALANCE_TABS,
  getTrialBalanceTabIcons,
} from 'constants/util/side-by-side-utils';
import { setSourceStatementElementMapForTrialBalance } from 'store/actions/side-by-side-statement/side-by-side-statement-actions';
import {
  performManualMappingInTrialBalance,
  clearTrialBalanceRowHighlight,
} from 'store/actions/trial-balance/trial-balance-action';
import {
  clearSideBySideElementsMap,
  clearSelectedElementsContentPanel,
} from 'store/actions/action-creators';
import {
  setTBSourceElementClicked,
  removeTBSourceElementClicked,
  removeTBTargetElementClicked,
} from 'store/actions/trial-balance/mapped-element-action';
import { isNullOrUndefined } from 'utils/object-utils';
import {
  STATEMENT_TYPES,
  AUTO_TIEOUT_COMPARE_MODE_FAILED,
} from 'constants/feature/statement-content-constants';
import { TB_PENDING_STEPS } from 'constants/feature/statement-creation-constants';
import { handleTBAutoTieoutElementClick } from 'utils/auto-tieout-element-utils';

const TRIAL_BALANCE_SNAPSHOT_VIEW_BLOCK = 'trial-balance-snapshot-view';
const CLOSE_ICON_DIMENSION = '28px';

const TrialBalanceSnapshotView = ({
  isSwapped,
  trialBalanceData,
  setTrialBalanceData,
  fetchTrialBalanceData,
  onCloseTab,
  isSnapshotReadOnly, // snapshot view if  snapshot is in read-only or user is an observer or statement is in being wrapped up
}) => {
  const dispatch = useDispatch();
  const { projectId, statementId, revisionId } = useParams();
  const [scrollToId, setScrollToId] = React.useState(null);
  const sideBySideElementMap = useSelector(
    (state) => state.ui.sideBySideView.sideBySideElementMap,
  );

  const {
    tbWorkFlowStatus,
    mappedElements,
    pendingAutoTieoutTBStep,
    rowHighlight,
  } = useSelector((state) => state.ui.sideBySideView.trialBalance);

  const {
    shouldHighlightMappedElements: { highlightMappedElements },
  } = useSelector((state) => state.ui.sideBySideView);
  const { isOCR } = useSelector((state) => state.data.selectedStatement);
  const { sectionsInView } = useSelector((state) => state.ui.statementPage);
  const { editPermissionAutotieout } = useSelector(
    (state) => state.data.editPermission,
  );

  const tbSourceElementClicked = mappedElements?.getSourceElementClicked?.();
  const tbTargetElementClicked = mappedElements?.getTargetElementClicked?.();
  const tbClickedElement = tbSourceElementClicked || tbTargetElementClicked;

  const selectedTab = useSelector(
    (state) =>
      state.ui.sideBySideView.trialBalance.scrolltoElementAndTableTab
        .selectedTab,
  );

  const elementToScroll = useSelector(
    (state) =>
      state.ui.sideBySideView.trialBalance.scrolltoElementAndTableTab
        .elementToScroll,
  );

  const onSelectTab = (tab) => {
    dispatch(setTrialBalanceTableTab(tab));
  };

  const getUpdatedTrialBalanceData = async () => {
    try {
      const response = await fetchTrialBalanceData();
      if (response?.status === StatusCodes.OK) {
        setTrialBalanceData(response?.data);
        // Based on this flag, we are initiating the auto tieout process in side-by-side-view.jsx file
        dispatch(
          updatePendingAutoTieoutTBStep(
            TB_PENDING_STEPS.INITIATE_TB_AUTO_TIEOUT,
          ),
        );
      }
    } catch (error) {
      dispatch(updatePendingAutoTieoutTBStep(TB_PENDING_STEPS.NONE));
      dispatch(
        setAutoTieoutStatusForBanner({
          status: AUTO_TIEOUT_COMPARE_MODE_FAILED,
          show: false,
        }),
      );
      console.error(
        'Failed to fetch updated trial balance snapshot data:',
        error,
      );
    }
  };

  useEffect(() => {
    dispatch(getTrialBalanceDetails());
  }, []);

  useEffect(() => {
    if (elementToScroll && trialBalanceData?.financialStatement) {
      const { rowId, tbWorkPaperType } = elementToScroll;

      const tabMapping = {
        [STATEMENT_TYPES.BALANCE_SHEET]: TRIAL_BALANCE_TABS.balanceSheet,
        [STATEMENT_TYPES.INCOME_STATEMENT]: TRIAL_BALANCE_TABS.incomeStatement,
      };

      const targetTab = tabMapping[tbWorkPaperType];

      if (targetTab) {
        if (selectedTab !== targetTab) {
          onSelectTab(targetTab);
        }
        setScrollToId(rowId);
      }
    }
  }, [
    elementToScroll.rowId,
    elementToScroll.tbWorkPaperType,
    trialBalanceData,
  ]);

  useEffect(() => {
    if (pendingAutoTieoutTBStep === TB_PENDING_STEPS.FETCH_UPDATED_SNAPSHOT) {
      getUpdatedTrialBalanceData();
    }
  }, [pendingAutoTieoutTBStep]);

  const { sourceMap } = mappedElements;
  const isTrialBalanceCompleted = tbWorkFlowStatus?.isCompleted;

  const onAmountClick = useCallback(
    (rowData) => {
      if (!isTrialBalanceCompleted) return;

      dispatch(clearTrialBalanceRowHighlight());
      dispatch(clearSelectedElementsContentPanel());

      const tbDataId = rowData?.id;

      const existingMapping = mappedElements?.getSourceMapping?.(tbDataId);
      const isRowAlreadyMapped =
        !isNullOrUndefined(existingMapping) && existingMapping.active;

      if (isRowAlreadyMapped) {
        const elementClickedId = tbSourceElementClicked?.tbDataId;

        if (
          !isNullOrUndefined(elementClickedId) &&
          tbDataId == elementClickedId
        ) {
          dispatch(removeTBSourceElementClicked());
          dispatch(removeTBTargetElementClicked());
          return;
        }

        handleTBAutoTieoutElementClick({
          rowElementsData: existingMapping,
          isTrialBalanceCompleted,
          mappedElements,
          sectionsInView,
          isOCR,
        });

        dispatch(clearSideBySideElementsMap());
        dispatch(removeTBTargetElementClicked());
        dispatch(setTBSourceElementClicked(existingMapping));
        return;
      }

      if (!isSnapshotReadOnly && editPermissionAutotieout) {
        dispatch(removeTBSourceElementClicked());
        dispatch(removeTBTargetElementClicked());

        dispatch(setSourceStatementElementMapForTrialBalance(rowData));

        const statementParams = { projectId, statementId, revisionId };
        dispatch(
          performManualMappingInTrialBalance({
            statementParams,
          }),
        );
      }
    },
    [
      isTrialBalanceCompleted,
      dispatch,
      projectId,
      statementId,
      revisionId,
      mappedElements,
      tbSourceElementClicked,
    ],
  );

  const tabRightIcons = getTrialBalanceTabIcons({
    BLOCK: TRIAL_BALANCE_SNAPSHOT_VIEW_BLOCK,
  });

  return (
    <div className={TRIAL_BALANCE_SNAPSHOT_VIEW_BLOCK}>
      <StatementHeader>
        <div
          className={classnames({
            [`${TRIAL_BALANCE_SNAPSHOT_VIEW_BLOCK}__header-wrap`]: true,
            [`${TRIAL_BALANCE_SNAPSHOT_VIEW_BLOCK}__header-wrap--swapped`]:
              isSwapped,
          })}
        >
          <div className={`${TRIAL_BALANCE_SNAPSHOT_VIEW_BLOCK}__title`}>
            <FormattedMessage id="trial-balance-snapshot-view.header.title" />
          </div>
          <div
            className={`${TRIAL_BALANCE_SNAPSHOT_VIEW_BLOCK}__last-import-date`}
          >
            {tbWorkFlowStatus?.processedDate && (
              <FormattedMessage
                id="trial-balance-view.last-import-date-time.label"
                values={{
                  dateTime: moment
                    .unix(tbWorkFlowStatus.processedDate)
                    .format('MM/DD/YYYY HH:mm:ss'),
                }}
              />
            )}
          </div>

          <CloseButton
            className={`${TRIAL_BALANCE_SNAPSHOT_VIEW_BLOCK}__cancelBtn`}
            onClick={onCloseTab}
            height={CLOSE_ICON_DIMENSION}
            width={CLOSE_ICON_DIMENSION}
            role="button"
          />
        </div>
      </StatementHeader>
      <StatementHeader>
        <TabsGroup
          id={`${TRIAL_BALANCE_SNAPSHOT_VIEW_BLOCK}-tabs`}
          className={`${TRIAL_BALANCE_SNAPSHOT_VIEW_BLOCK}__tabs`}
          tabsArray={TRIAL_BALANCE_TABS_ARRAY}
          selectedTab={selectedTab}
          onSelectTab={onSelectTab}
          rightIcon={tabRightIcons}
        />
      </StatementHeader>
      {selectedTab === TRIAL_BALANCE_TABS.balanceSheet ? (
        <BalanceSheetView
          trialBalanceData={trialBalanceData}
          scrollToId={scrollToId}
          mappedElements={sourceMap}
          onAmountClick={onAmountClick}
          sideBySideElementMap={sideBySideElementMap}
          cellHighlight={rowHighlight}
          tbClickedElement={tbClickedElement}
          highlightMappedElements={highlightMappedElements}
        />
      ) : (
        <IncomeStatementView
          trialBalanceData={trialBalanceData}
          mappedElements={sourceMap}
          scrollToId={scrollToId}
          onAmountClick={onAmountClick}
          sideBySideElementMap={sideBySideElementMap}
          cellHighlight={rowHighlight}
          tbClickedElement={tbClickedElement}
          highlightMappedElements={highlightMappedElements}
        />
      )}
    </div>
  );
};
export default TrialBalanceSnapshotView;
